import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  modelsApi,
  foundryConnectionsApi,
  MODALITY_LABELS,
  type ModelCatalogueItem,
  type ModelModality,
  type FoundryConnection,
  type FoundryDeploymentDiscovered,
} from "@/api/models";
import { companiesApi } from "@/api/companies";
import { queryKeys } from "@/lib/query-keys";
import {
  Plus, Trash2, Star, Download, Key,
  Power, PowerOff, Check, X, ChevronDown, ChevronRight,
  FlaskConical, Loader2, Cloud, Import, Zap, RefreshCw,
  Plug, Pencil, Globe, Lock, Save,
} from "lucide-react";

// --- Provider logo SVGs (inline for reliability) ---

function AnthropicLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 256 176" className={className} fill="currentColor">
      <path d="M147.487 0L256 176h-54.264L93.224 0h54.263ZM108.513 0L0 176h54.264l108.512-176H108.513Z" />
    </svg>
  );
}

function OpenAILogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.998 5.998 0 0 0-3.998 2.9 6.042 6.042 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073ZM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494ZM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646ZM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872v.024Zm16.597 3.855-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667Zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66v.018ZM8.318 12.232l-2.026-1.168a.071.071 0 0 1-.038-.052V5.422a4.497 4.497 0 0 1 7.378-3.456l-.142.08L8.704 4.81a.795.795 0 0 0-.392.68l-.003 6.742h.009Zm1.1-2.367 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5-.005-2.999Z" />
    </svg>
  );
}

function GoogleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62Z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53Z" />
    </svg>
  );
}

function AzureLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 96 96" className={className}>
      <defs>
        <linearGradient id="azure-a" x1="58.97" y1="9.37" x2="26.93" y2="87.09" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#114a8b" />
          <stop offset="1" stopColor="#0669bc" />
        </linearGradient>
        <linearGradient id="azure-b" x1="56.56" y1="44.09" x2="49.7" y2="46.69" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopOpacity=".3" />
          <stop offset=".07" stopOpacity=".2" />
          <stop offset="1" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="azure-c" x1="50.16" y1="7.67" x2="79.94" y2="85.45" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#3ccbf4" />
          <stop offset="1" stopColor="#2892df" />
        </linearGradient>
      </defs>
      <path d="M33.34 6.54h26.04L33.12 89.02a4.59 4.59 0 0 1-4.33 3.06H12.06a4.58 4.58 0 0 1-4.33-6.12L32.61 9.6a4.59 4.59 0 0 1 4.33-3.06h.4Z" fill="url(#azure-a)" />
      <path d="M67.34 60.76H30.34a2.08 2.08 0 0 0-1.42 3.6l23.79 22.2a4.64 4.64 0 0 0 3.16 1.24H72.2Z" fill="url(#azure-b)" />
      <path d="M33.34 6.54a4.56 4.56 0 0 0-4.37 3.16L4.02 85.84a4.58 4.58 0 0 0 4.33 6.24h17.32a4.66 4.66 0 0 0 3.6-2.43l5.24-10.2 18.57 12.3a4.57 4.57 0 0 0 2.5.75h16.31L59.33 6.54Z" fill="url(#azure-c)" />
    </svg>
  );
}

const PROVIDER_CONFIG: Record<string, { label: string; color: string; bgColor: string; Logo: React.FC<{ className?: string }> }> = {
  anthropic: { label: "Anthropic", color: "text-[#d97757]", bgColor: "bg-[#d97757]/10", Logo: AnthropicLogo },
  openai: { label: "OpenAI", color: "text-[#10a37f]", bgColor: "bg-[#10a37f]/10", Logo: OpenAILogo },
  google: { label: "Google", color: "text-[#4285f4]", bgColor: "bg-[#4285f4]/10", Logo: GoogleLogo },
  azure_foundry: { label: "Azure Foundry", color: "text-[#0078d4]", bgColor: "bg-[#0078d4]/10", Logo: AzureLogo },
};

function getProviderConfig(provider: string) {
  return PROVIDER_CONFIG[provider] || {
    label: provider,
    color: "text-text-muted",
    bgColor: "bg-surface-hover",
    Logo: () => <div className="h-5 w-5 rounded bg-surface-hover" />,
  };
}

function formatCost(cents: number | null): string {
  if (cents == null) return "N/A";
  if (cents >= 100) return `$${(cents / 100).toFixed(0)}`;
  return `$${(cents / 100).toFixed(2)}`;
}

function formatContext(tokens: number | null): string {
  if (tokens == null) return "";
  if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
  return `${Math.round(tokens / 1000)}K`;
}

export function ModelCatalogue() {
  const queryClient = useQueryClient();
  const { data: items = [], isLoading } = useQuery({
    queryKey: queryKeys.models.all,
    queryFn: modelsApi.listAll,
  });
  const { data: apiKeyStatus } = useQuery({
    queryKey: ["models", "api-keys"],
    queryFn: modelsApi.getApiKeyStatus,
  });
  const { data: connections = [] } = useQuery({
    queryKey: ["foundry-connections"],
    queryFn: foundryConnectionsApi.list,
  });
  const { data: companies = [] } = useQuery({
    queryKey: ["companies"],
    queryFn: companiesApi.list,
  });
  const [showCreate, setShowCreate] = useState(false);
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [showConnections, setShowConnections] = useState(false);
  const [apiKeyForm, setApiKeyForm] = useState<{ provider: string; value: string }>({ provider: "", value: "" });
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; latencyMs?: number; response?: string; error?: string; testing: boolean }>>({});

  // Foundry connection management
  const [connForm, setConnForm] = useState<{ id?: string; name: string; region: string; endpoint: string; apiKey: string } | null>(null);
  const [discoverConnId, setDiscoverConnId] = useState<string | null>(null);
  const [deployments, setDeployments] = useState<FoundryDeploymentDiscovered[]>([]);
  const [selectedDeployments, setSelectedDeployments] = useState<Set<string>>(new Set());

  // Per-model tenant availability editor
  const [accessEditor, setAccessEditor] = useState<{ modelId: string; allTenants: boolean; tenantIds: Set<string>; loading: boolean } | null>(null);

  const [form, setForm] = useState({
    provider: "",
    modelId: "",
    displayName: "",
    costPer1mInput: "",
    costPer1mOutput: "",
    contextWindow: "",
    category: "chat",
  });

  const seedMutation = useMutation({
    mutationFn: () => modelsApi.seed(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.models.all }),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      modelsApi.create({
        provider: form.provider,
        modelId: form.modelId,
        displayName: form.displayName,
        costPer1mInput: form.costPer1mInput ? Number(form.costPer1mInput) : null,
        costPer1mOutput: form.costPer1mOutput ? Number(form.costPer1mOutput) : null,
        contextWindow: form.contextWindow ? Number(form.contextWindow) : null,
        category: form.category,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.models.all });
      setShowCreate(false);
      setForm({ provider: "", modelId: "", displayName: "", costPer1mInput: "", costPer1mOutput: "", contextWindow: "", category: "chat" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      modelsApi.update(id, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.models.all }),
  });

  const defaultMutation = useMutation({
    mutationFn: (id: string) => modelsApi.update(id, { isDefault: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.models.all }),
  });

  // Manual modality override. Discovery classifies on import, but it can only
  // work from the provider's capability map and the deployment name — anything
  // it can't place lands as "other" and needs setting by hand here. An explicit
  // choice also survives re-syncs (see upsertFoundryDeployments).
  const modalityMutation = useMutation({
    mutationFn: ({ id, modality }: { id: string; modality: ModelModality }) =>
      modelsApi.update(id, { modality }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.models.all }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => modelsApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.models.all }),
  });

  const setApiKeyMutation = useMutation({
    mutationFn: () => modelsApi.setApiKey(apiKeyForm.provider, apiKeyForm.value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["models", "api-keys"] });
      setApiKeyForm({ provider: "", value: "" });
    },
  });

  async function handleTestModel(id: string) {
    setTestResults((prev) => ({ ...prev, [id]: { success: false, testing: true } }));
    try {
      const result = await modelsApi.testModel(id);
      setTestResults((prev) => ({ ...prev, [id]: { ...result, testing: false } }));
    } catch {
      setTestResults((prev) => ({ ...prev, [id]: { success: false, error: "Request failed", testing: false } }));
    }
  }

  const syncFoundryMutation = useMutation({
    mutationFn: () => modelsApi.syncFoundry(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.models.all }),
  });

  const invalidateConns = () =>
    queryClient.invalidateQueries({ queryKey: ["foundry-connections"] });

  const saveConnMutation = useMutation({
    mutationFn: () => {
      if (!connForm) throw new Error("no form");
      const payload = {
        name: connForm.name,
        region: connForm.region || undefined,
        endpoint: connForm.endpoint,
        ...(connForm.apiKey ? { apiKey: connForm.apiKey } : {}),
      };
      return connForm.id
        ? foundryConnectionsApi.update(connForm.id, payload)
        : foundryConnectionsApi.create(payload);
    },
    onSuccess: () => { invalidateConns(); setConnForm(null); },
  });

  const deleteConnMutation = useMutation({
    mutationFn: (id: string) => foundryConnectionsApi.remove(id),
    onSuccess: () => invalidateConns(),
  });

  const discoverConnMutation = useMutation({
    mutationFn: (id: string) => foundryConnectionsApi.discover(id),
    onSuccess: (data, id) => {
      const deps = data.deployments || [];
      setDeployments(deps);
      setSelectedDeployments(new Set(deps.map((d) => d.deploymentName)));
      setDiscoverConnId(id);
    },
  });

  const importConnMutation = useMutation({
    mutationFn: (id: string) => {
      const chosen = deployments.filter((d) => selectedDeployments.has(d.deploymentName));
      return foundryConnectionsApi.import(id, chosen);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.models.all });
      setDeployments([]);
      setSelectedDeployments(new Set());
      setDiscoverConnId(null);
    },
  });

  const syncConnMutation = useMutation({
    mutationFn: (id: string) => foundryConnectionsApi.sync(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.models.all }),
  });

  const saveAccessMutation = useMutation({
    mutationFn: () => {
      if (!accessEditor) throw new Error("no editor");
      return modelsApi.setAccess(accessEditor.modelId, {
        allTenants: accessEditor.allTenants,
        tenantIds: [...accessEditor.tenantIds],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.models.all });
      setAccessEditor(null);
    },
  });

  async function openAccess(id: string) {
    setAccessEditor({ modelId: id, allTenants: false, tenantIds: new Set(), loading: true });
    try {
      const a = await modelsApi.getAccess(id);
      setAccessEditor({ modelId: id, allTenants: a.allTenants, tenantIds: new Set(a.tenantIds), loading: false });
    } catch {
      setAccessEditor({ modelId: id, allTenants: false, tenantIds: new Set(), loading: false });
    }
  }

  // Group models by provider
  const grouped = items.reduce<Record<string, ModelCatalogueItem[]>>((acc, item) => {
    if (!acc[item.provider]) acc[item.provider] = [];
    acc[item.provider].push(item);
    return acc;
  }, {});

  // Ensure known providers appear even if empty
  for (const p of Object.keys(PROVIDER_CONFIG)) {
    if (!grouped[p]) grouped[p] = [];
  }

  const providerOrder = ["anthropic", "openai", "google", "azure_foundry", ...Object.keys(grouped).filter((p) => !PROVIDER_CONFIG[p])];

  return (
    <CenterPanel title="Model Catalogue">
      {/* Actions bar */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {items.length} model{items.length !== 1 ? "s" : ""} across {Object.keys(grouped).length} providers
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => setShowApiKeys(!showApiKeys)}>
            <Key size={14} className="mr-1.5" />
            API Keys
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => seedMutation.mutate()}
            disabled={seedMutation.isPending}
          >
            <Download size={14} className="mr-1.5" />
            {seedMutation.isPending ? "Seeding..." : "Seed Latest"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => syncFoundryMutation.mutate()}
            disabled={syncFoundryMutation.isPending}
            title="Re-discover models from all Foundry connections and update the catalogue"
          >
            {syncFoundryMutation.isPending ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <RefreshCw size={14} className="mr-1.5" />
            )}
            {syncFoundryMutation.isPending ? "Syncing..." : "Sync All"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowConnections(!showConnections)}>
            <Plug size={14} className="mr-1.5" />
            Connections
          </Button>
          <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
            <Plus size={14} className="mr-1.5" />
            Add Model
          </Button>
        </div>
      </div>

      {/* Seed result toast */}
      {seedMutation.isSuccess && (
        <div className="mb-3 rounded-lg border border-success/30 bg-success/5 px-3 py-2 text-sm text-success">
          Added {seedMutation.data.added} models, {seedMutation.data.skipped} already existed.
        </div>
      )}

      {/* Foundry sync result toast */}
      {syncFoundryMutation.isSuccess && (
        <div className="mb-3 rounded-lg border border-success/30 bg-success/5 px-3 py-2 text-sm text-success">
          Foundry sync: {syncFoundryMutation.data.added} added, {syncFoundryMutation.data.updated} updated, {syncFoundryMutation.data.total} total deployments.
        </div>
      )}
      {syncFoundryMutation.isError && (
        <div className="mb-3 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
          Foundry sync failed: {(syncFoundryMutation.error as Error)?.message || "Unknown error"}
        </div>
      )}

      {/* API Keys Panel */}
      {showApiKeys && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Provider API Keys</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-text-muted">
              API keys are stored in Azure Key Vault. Agents resolve keys at runtime via managed identity.
            </p>
            <div className="space-y-2">
              {Object.entries(PROVIDER_CONFIG).map(([provider, cfg]) => {
                const status = apiKeyStatus?.[provider];
                const Logo = cfg.Logo;
                return (
                  <div key={provider} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2">
                    <div className={`flex h-7 w-7 items-center justify-center rounded-md ${cfg.bgColor}`}>
                      <Logo className={`h-4 w-4 ${cfg.color}`} />
                    </div>
                    <span className="text-sm font-medium text-text-primary flex-1">{cfg.label}</span>
                    {status?.configured ? (
                      <Badge variant="success" className="text-[10px]">
                        <Check size={10} className="mr-0.5" />
                        {status.maskedValue}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Not Set</Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      onClick={() => setApiKeyForm({ provider, value: "" })}
                    >
                      {status?.configured ? "Update" : "Set Key"}
                    </Button>
                  </div>
                );
              })}
            </div>
            {apiKeyForm.provider && (
              <div className="flex gap-2 items-center rounded-lg border border-accent/30 bg-accent/5 p-3">
                <span className="text-sm text-text-secondary whitespace-nowrap">
                  {PROVIDER_CONFIG[apiKeyForm.provider]?.label || apiKeyForm.provider}:
                </span>
                <Input
                  type="password"
                  placeholder="Paste API key..."
                  className="flex-1 text-sm"
                  value={apiKeyForm.value}
                  onChange={(e) => setApiKeyForm({ ...apiKeyForm, value: e.target.value })}
                />
                <Button
                  size="sm"
                  disabled={!apiKeyForm.value.trim() || setApiKeyMutation.isPending}
                  onClick={() => setApiKeyMutation.mutate()}
                >
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setApiKeyForm({ provider: "", value: "" })}>
                  <X size={14} />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Azure Foundry Connections */}
      {showConnections && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <AzureLogo className="h-4 w-4" />
                Azure Foundry Connections
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={() => setConnForm({ name: "", region: "", endpoint: "", apiKey: "" })}
              >
                <Plus size={13} className="mr-1" /> Add connection
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-text-muted">
              Each connection points at one Azure AI Foundry project (e.g. a region) with its own API key.
              Discover and import only the models you need; keys are stored in Key Vault.
            </p>

            {/* Add / edit form */}
            {connForm && (
              <div className="space-y-2 rounded-lg border border-accent/30 bg-accent/5 p-3">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Input
                    placeholder="Name (e.g. UK South)"
                    value={connForm.name}
                    onChange={(e) => setConnForm({ ...connForm, name: e.target.value })}
                    className="text-sm"
                  />
                  <Input
                    placeholder="Region (optional, e.g. uksouth)"
                    value={connForm.region}
                    onChange={(e) => setConnForm({ ...connForm, region: e.target.value })}
                    className="text-sm"
                  />
                </div>
                <Input
                  placeholder="Endpoint (https://my-resource.services.ai.azure.com)"
                  value={connForm.endpoint}
                  onChange={(e) => setConnForm({ ...connForm, endpoint: e.target.value })}
                  className="text-sm"
                />
                <Input
                  type="password"
                  placeholder={connForm.id ? "API key (leave blank to keep existing)" : "API key (from portal → Project → Keys)"}
                  value={connForm.apiKey}
                  onChange={(e) => setConnForm({ ...connForm, apiKey: e.target.value })}
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={!connForm.name.trim() || !connForm.endpoint.trim() || saveConnMutation.isPending}
                    onClick={() => saveConnMutation.mutate()}
                  >
                    {saveConnMutation.isPending ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Save size={13} className="mr-1.5" />}
                    {connForm.id ? "Save" : "Create"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConnForm(null)}>Cancel</Button>
                </div>
              </div>
            )}

            {/* Connection list */}
            {connections.length === 0 && !connForm && (
              <p className="text-xs text-text-muted">No connections yet. Add one to discover Foundry models.</p>
            )}
            <div className="space-y-2">
              {connections.map((c: FoundryConnection) => (
                <div key={c.id} className="rounded-lg border border-border">
                  <div className="flex items-center gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-text-primary">{c.name}</span>
                        {c.region && <Badge variant="outline" className="text-[9px]">{c.region}</Badge>}
                        {c.isDefault && <Badge variant="secondary" className="text-[9px]">Default</Badge>}
                        {c.keyConfigured ? (
                          <Badge variant="success" className="text-[9px]"><Key size={8} className="mr-0.5" />{c.maskedKey}</Badge>
                        ) : (
                          <Badge variant="warning" className="text-[9px]">No key</Badge>
                        )}
                        {!c.enabled && <Badge variant="outline" className="text-[9px]">Disabled</Badge>}
                      </div>
                      <p className="mt-0.5 truncate text-[11px] font-mono text-text-muted">{c.endpoint}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      disabled={discoverConnMutation.isPending}
                      onClick={() => discoverConnMutation.mutate(c.id)}
                    >
                      {discoverConnMutation.isPending && discoverConnMutation.variables === c.id
                        ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Cloud size={13} className="mr-1" />}
                      Discover
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      title="Sync this connection"
                      disabled={syncConnMutation.isPending}
                      onClick={() => syncConnMutation.mutate(c.id)}
                    >
                      {syncConnMutation.isPending && syncConnMutation.variables === c.id
                        ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} className="text-text-muted" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      title="Edit"
                      onClick={() => setConnForm({ id: c.id, name: c.name, region: c.region || "", endpoint: c.endpoint, apiKey: "" })}
                    >
                      <Pencil size={13} className="text-text-muted" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      title="Delete"
                      onClick={() => deleteConnMutation.mutate(c.id)}
                    >
                      <Trash2 size={13} className="text-text-muted hover:text-danger" />
                    </Button>
                  </div>

                  {/* Discovery results for this connection */}
                  {discoverConnId === c.id && deployments.length > 0 && (
                    <div className="space-y-1 border-t border-border px-3 py-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-text-secondary">
                          {selectedDeployments.size} of {deployments.length} selected
                        </span>
                        <Button
                          size="sm"
                          disabled={selectedDeployments.size === 0 || importConnMutation.isPending}
                          onClick={() => importConnMutation.mutate(c.id)}
                        >
                          {importConnMutation.isPending ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Import size={13} className="mr-1.5" />}
                          Import selected ({selectedDeployments.size})
                        </Button>
                      </div>
                      <div className="max-h-48 space-y-1 overflow-y-auto">
                        {deployments.map((d) => {
                          const checked = selectedDeployments.has(d.deploymentName);
                          return (
                            <label
                              key={d.deploymentName}
                              className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setSelectedDeployments((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(d.deploymentName)) next.delete(d.deploymentName);
                                    else next.add(d.deploymentName);
                                    return next;
                                  });
                                }}
                              />
                              <span className="font-medium text-text-primary">{d.displayName}</span>
                              <span className="text-xs text-text-muted">{d.modelPublisher}</span>
                              <span className="ml-auto font-mono text-xs text-text-muted">{d.deploymentName}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {discoverConnId === c.id && deployments.length === 0 && !discoverConnMutation.isPending && (
                    <p className="border-t border-border px-3 py-2 text-xs text-text-muted">No deployments found.</p>
                  )}
                </div>
              ))}
            </div>

            {discoverConnMutation.isError && (
              <p className="text-xs text-danger">
                Discovery failed: {(discoverConnMutation.error as Error)?.message || "Unknown error"}
              </p>
            )}
            {deleteConnMutation.isError && (
              <p className="text-xs text-danger">
                {(deleteConnMutation.error as Error)?.message || "Delete failed"}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create form */}
      {showCreate && (
        <Card className="mb-4">
          <CardContent className="space-y-3 pt-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Input
                placeholder="Provider (e.g. anthropic)"
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
              />
              <Input
                placeholder="Model ID (e.g. claude-sonnet-4-6)"
                value={form.modelId}
                onChange={(e) => setForm({ ...form, modelId: e.target.value })}
              />
              <Input
                placeholder="Display Name"
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Input
                placeholder="Cost per 1M input (cents)"
                type="number"
                value={form.costPer1mInput}
                onChange={(e) => setForm({ ...form, costPer1mInput: e.target.value })}
              />
              <Input
                placeholder="Cost per 1M output (cents)"
                type="number"
                value={form.costPer1mOutput}
                onChange={(e) => setForm({ ...form, costPer1mOutput: e.target.value })}
              />
              <Input
                placeholder="Context window (tokens)"
                type="number"
                value={form.contextWindow}
                onChange={(e) => setForm({ ...form, contextWindow: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!form.provider.trim() || !form.modelId.trim() || !form.displayName.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                Create
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Model list grouped by provider */}
      {isLoading ? (
        <p className="text-sm text-text-muted">Loading models...</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Download size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary mb-3">No models in catalogue yet.</p>
            <Button size="sm" onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending}>
              <Download size={14} className="mr-1.5" />
              Seed Latest Models
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {providerOrder
            .filter((p) => grouped[p] && grouped[p].length > 0)
            .map((provider) => {
              const models = grouped[provider];
              const cfg = getProviderConfig(provider);
              const Logo = cfg.Logo;
              const isExpanded = expandedProvider === null || expandedProvider === provider;
              const keyStatus = apiKeyStatus?.[provider];

              return (
                <div key={provider}>
                  {/* Provider header */}
                  <button
                    className="flex w-full items-center gap-2.5 mb-2 group cursor-pointer"
                    onClick={() => setExpandedProvider(expandedProvider === provider ? null : provider)}
                  >
                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${cfg.bgColor}`}>
                      <Logo className={`h-5 w-5 ${cfg.color}`} />
                    </div>
                    <span className="text-sm font-semibold text-text-primary">{cfg.label}</span>
                    <span className="text-xs text-text-muted">{models.length} model{models.length !== 1 ? "s" : ""}</span>
                    {keyStatus?.configured ? (
                      <Badge variant="success" className="text-[9px] px-1.5 py-0">
                        <Key size={8} className="mr-0.5" /> Key Set
                      </Badge>
                    ) : (
                      <Badge variant="warning" className="text-[9px] px-1.5 py-0">
                        No Key
                      </Badge>
                    )}
                    <div className="flex-1" />
                    {isExpanded ? <ChevronDown size={14} className="text-text-muted" /> : <ChevronRight size={14} className="text-text-muted" />}
                  </button>

                  {/* Models */}
                  {isExpanded && (
                    <div className="flex flex-col gap-1.5 ml-10 mb-4">
                      {models.map((item) => (
                        <div key={item.id}>
                        <div
                          className={`flex items-center justify-between rounded-lg border px-3 py-2.5 transition-colors ${
                            item.enabled
                              ? "border-border bg-surface hover:bg-surface-hover"
                              : "border-border/50 bg-surface/50 opacity-60"
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-text-primary">{item.displayName}</p>
                                {item.isDefault && (
                                  <Badge variant="secondary" className="text-[9px] px-1.5 py-0">
                                    <Star size={8} className="mr-0.5" /> Default
                                  </Badge>
                                )}
                                {/* Modality is the load-bearing badge: a non-text
                                    model must never be picked as an agent's
                                    conversational model, so make it obvious. */}
                                {(item.modality ?? "text") !== "text" && (
                                  <Badge
                                    variant="outline"
                                    className={`text-[9px] px-1.5 py-0 ${
                                      item.modality === "other"
                                        ? "text-amber-600 border-amber-500/40"
                                        : "text-violet-600 border-violet-500/40"
                                    }`}
                                    title={
                                      item.modality === "other"
                                        ? "Discovery could not classify this deployment. Set its modality below so it appears in the right picker."
                                        : `${MODALITY_LABELS[item.modality]} model — not selectable as an agent's conversational model.`
                                    }
                                  >
                                    {MODALITY_LABELS[item.modality] ?? item.modality}
                                  </Badge>
                                )}
                                {(item.modality ?? "text") === "text" && item.category !== "chat" && (
                                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 capitalize">
                                    {item.category}
                                  </Badge>
                                )}
                                {item.provider === "azure_foundry" && (() => {
                                  // Which Foundry connection this model synced from —
                                  // otherwise identical deployments across regions are
                                  // indistinguishable. Resolve via connectionId; legacy
                                  // rows (no id) fall back to the endpoint's resource.
                                  const meta = (item.metadata ?? {}) as Record<string, unknown>;
                                  const conn = connections.find((c) => c.id === meta.foundryConnectionId);
                                  const label = conn
                                    ? `${conn.name}${conn.region ? ` · ${conn.region}` : ""}`
                                    : typeof meta.foundryEndpoint === "string" && meta.foundryEndpoint
                                      ? (meta.foundryEndpoint as string).replace(/^https?:\/\//, "").split(".")[0]
                                      : null;
                                  return label ? (
                                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-[#0078d4] border-[#0078d4]/40" title={typeof meta.foundryEndpoint === "string" ? (meta.foundryEndpoint as string) : undefined}>
                                      {label}
                                    </Badge>
                                  ) : null;
                                })()}
                              </div>
                              <div className="flex items-center gap-3 mt-0.5">
                                <span className="text-[11px] text-text-muted font-mono">{item.modelId}</span>
                                {item.contextWindow && (
                                  <span className="text-[10px] text-text-muted">
                                    {formatContext(item.contextWindow)} ctx
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-[11px] text-text-secondary whitespace-nowrap">
                              {formatCost(item.costPer1mInput)} / {formatCost(item.costPer1mOutput)}
                            </span>
                            {/* Test button */}
                            {(() => {
                              const tr = testResults[item.id];
                              if (tr?.testing) {
                                return (
                                  <div className="flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5">
                                    <Loader2 size={10} className="animate-spin text-accent" />
                                    <span className="text-[9px] text-accent">Testing...</span>
                                  </div>
                                );
                              }
                              if (tr && !tr.testing) {
                                return (
                                  <button
                                    className={`flex items-center gap-1 rounded-full px-2 py-0.5 cursor-pointer ${
                                      tr.success ? "bg-success/10" : "bg-danger/10"
                                    }`}
                                    title={tr.success ? `OK (${tr.latencyMs}ms): ${tr.response}` : tr.error || "Failed"}
                                    onClick={() => handleTestModel(item.id)}
                                  >
                                    {tr.success ? (
                                      <>
                                        <Zap size={9} className="text-success" />
                                        <span className="text-[9px] text-success font-medium">{tr.latencyMs}ms</span>
                                      </>
                                    ) : (
                                      <>
                                        <X size={9} className="text-danger" />
                                        <span className="text-[9px] text-danger font-medium truncate max-w-[80px]">{tr.error}</span>
                                      </>
                                    )}
                                  </button>
                                );
                              }
                              return (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  title="Test model connection"
                                  onClick={() => handleTestModel(item.id)}
                                >
                                  <FlaskConical size={13} className="text-text-muted" />
                                </Button>
                              );
                            })()}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              title={item.enabled ? "Disable" : "Enable"}
                              onClick={() => toggleMutation.mutate({ id: item.id, enabled: !item.enabled })}
                            >
                              {item.enabled ? <Power size={13} className="text-success" /> : <PowerOff size={13} className="text-text-muted" />}
                            </Button>
                            {!item.isDefault && item.enabled && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                title="Set as default"
                                onClick={() => defaultMutation.mutate(item.id)}
                              >
                                <Star size={13} className="text-text-muted" />
                              </Button>
                            )}
                            <select
                              className="h-7 rounded-md border border-border bg-surface px-1.5 text-[10px] text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent"
                              title="Modality — what this model produces. Only text models can be an agent's conversational model."
                              value={item.modality ?? "text"}
                              disabled={modalityMutation.isPending}
                              onChange={(e) =>
                                modalityMutation.mutate({
                                  id: item.id,
                                  modality: e.target.value as ModelModality,
                                })
                              }
                            >
                              {(Object.keys(MODALITY_LABELS) as ModelModality[]).map((m) => (
                                <option key={m} value={m}>
                                  {MODALITY_LABELS[m]}
                                </option>
                              ))}
                            </select>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              title="Tenant availability"
                              onClick={() => openAccess(item.id)}
                            >
                              {item.availableToAllTenants ? (
                                <><Globe size={12} className="mr-1 text-success" /><span className="text-[10px]">All</span></>
                              ) : (
                                <><Lock size={12} className="mr-1 text-text-muted" /><span className="text-[10px]">Tenants</span></>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => deleteMutation.mutate(item.id)}
                            >
                              <Trash2 size={13} className="text-text-muted hover:text-danger" />
                            </Button>
                          </div>
                        </div>

                        {/* Tenant availability editor */}
                        {accessEditor?.modelId === item.id && (
                          <div className="mt-1 rounded-lg border border-accent/30 bg-accent/5 p-3">
                            {accessEditor.loading ? (
                              <p className="flex items-center gap-1 text-xs text-text-muted">
                                <Loader2 size={12} className="animate-spin" /> Loading…
                              </p>
                            ) : (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-text-secondary">
                                  Which tenants can use {item.displayName}?
                                </p>
                                <div className="flex gap-4 text-xs">
                                  <label className="flex cursor-pointer items-center gap-1.5">
                                    <input
                                      type="radio"
                                      checked={accessEditor.allTenants}
                                      onChange={() => setAccessEditor({ ...accessEditor, allTenants: true })}
                                    />
                                    All tenants
                                  </label>
                                  <label className="flex cursor-pointer items-center gap-1.5">
                                    <input
                                      type="radio"
                                      checked={!accessEditor.allTenants}
                                      onChange={() => setAccessEditor({ ...accessEditor, allTenants: false })}
                                    />
                                    Selected tenants
                                  </label>
                                </div>
                                {!accessEditor.allTenants && (
                                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                                    {companies.length === 0 && (
                                      <p className="text-xs text-text-muted">No tenants found.</p>
                                    )}
                                    {companies.map((co) => {
                                      const checked = accessEditor.tenantIds.has(co.id);
                                      return (
                                        <label key={co.id} className="flex cursor-pointer items-center gap-2 text-xs">
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() =>
                                              setAccessEditor((prev) => {
                                                if (!prev) return prev;
                                                const next = new Set(prev.tenantIds);
                                                if (next.has(co.id)) next.delete(co.id);
                                                else next.add(co.id);
                                                return { ...prev, tenantIds: next };
                                              })
                                            }
                                          />
                                          {co.name}
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                                <div className="flex gap-2">
                                  <Button size="sm" disabled={saveAccessMutation.isPending} onClick={() => saveAccessMutation.mutate()}>
                                    {saveAccessMutation.isPending ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Check size={13} className="mr-1.5" />}
                                    Save
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setAccessEditor(null)}>Cancel</Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </CenterPanel>
  );
}
